import React, { useState, useEffect } from 'react';
import { generateBlog, getKnowledgeCategories, getTrendingKeywords } from '../../api/blogApi';
import './BlogGenerationForm.scss';

const BlogGenerationForm = ({ onBlogGenerated, clinicId }) => {
  const [formData, setFormData] = useState({
    topic: '',
    word_count: 800,
    language: 'en',
    target_keywords: [],
    clinic_id: clinicId || '',
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [knowledgeCategories, setKnowledgeCategories] = useState([]);
  const [trendingKeywords, setTrendingKeywords] = useState([]);
  const [suggestedTopics, setSuggestedTopics] = useState([]);

  const languageOptions = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'mr', name: 'Marathi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'bn', name: 'Bengali' },
    { code: 'pa', name: 'Punjabi' },
  ];

  const wordCountOptions = [
    { value: 300, label: 'Short (300 words)' },
    { value: 600, label: 'Medium (600 words)' },
    { value: 800, label: 'Standard (800 words)' },
    { value: 1000, label: 'Long (1000 words)' },
    { value: 1500, label: 'Comprehensive (1500 words)' },
    { value: 2000, label: 'Detailed (2000 words)' },
  ];

  const suggestedTopicsList = [
    'Dental Care Tips for Local Residents',
    'Common Dental Problems and Solutions',
    'Best Practices for Oral Hygiene',
    'When to Visit a Dentist',
    'Dental Treatments Available',
    'Emergency Dental Care Guide',
    'Children\'s Dental Health',
    'Senior Dental Care Tips',
    'Cosmetic Dentistry Options',
    'Preventive Dental Care',
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [categories, keywords] = await Promise.all([
        getKnowledgeCategories(),
        getTrendingKeywords(),
      ]);
      
      setKnowledgeCategories(categories);
      setTrendingKeywords(keywords);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleKeywordChange = (e) => {
    const keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k);
    setFormData(prev => ({
      ...prev,
      target_keywords: keywords
    }));
  };

  const addKeyword = (keyword) => {
    if (!formData.target_keywords.includes(keyword)) {
      setFormData(prev => ({
        ...prev,
        target_keywords: [...prev.target_keywords, keyword]
      }));
    }
  };

  const removeKeyword = (keywordToRemove) => {
    setFormData(prev => ({
      ...prev,
      target_keywords: prev.target_keywords.filter(k => k !== keywordToRemove)
    }));
  };

  const selectSuggestedTopic = (topic) => {
    setFormData(prev => ({
      ...prev,
      topic: topic
    }));
  };

  const validateForm = () => {
    if (!formData.topic.trim()) {
      setError('Please enter a blog topic');
      return false;
    }
    if (!formData.clinic_id) {
      setError('Please select a clinic');
      return false;
    }
    if (formData.target_keywords.length === 0) {
      setError('Please add at least one target keyword');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsGenerating(true);
    setGenerationProgress(0);
    setError('');
    setSuccess('');

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      const result = await generateBlog(formData);
      
      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (result.success) {
        setSuccess('Blog generated successfully!');
        if (onBlogGenerated) {
          onBlogGenerated(result.blog_post);
        }
        
        // Reset form
        setFormData({
          topic: '',
          word_count: 800,
          language: 'en',
          target_keywords: [],
          clinic_id: clinicId || '',
        });
      } else {
        setError(result.error || 'Failed to generate blog');
      }
    } catch (error) {
      setError('An error occurred during blog generation');
      console.error('Blog generation error:', error);
    } finally {
      setIsGenerating(false);
      setTimeout(() => setGenerationProgress(0), 2000);
    }
  };

  return (
    <div className="blog-generation-form">
      <div className="form-header">
        <h3>🤖 AI Blog Generator</h3>
        <p>Generate SEO-optimized blog content using LangChain and RAG</p>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8">
            <div className="form-group">
              <label htmlFor="topic">Blog Topic *</label>
              <input
                type="text"
                id="topic"
                name="topic"
                value={formData.topic}
                onChange={handleInputChange}
                placeholder="e.g., Root Canal Treatment in Mumbai"
                className="form-control"
                required
              />
            </div>
          </div>
          
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="language">Language</label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                className="form-control"
              >
                {languageOptions.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="word_count">Word Count</label>
              <select
                id="word_count"
                name="word_count"
                value={formData.word_count}
                onChange={handleInputChange}
                className="form-control"
              >
                {wordCountOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="clinic_id">Clinic ID</label>
              <input
                type="text"
                id="clinic_id"
                name="clinic_id"
                value={formData.clinic_id}
                onChange={handleInputChange}
                placeholder="Enter clinic ID"
                className="form-control"
                required
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="target_keywords">Target Keywords *</label>
          <input
            type="text"
            id="target_keywords"
            name="target_keywords"
            value={formData.target_keywords.join(', ')}
            onChange={handleKeywordChange}
            placeholder="Enter keywords separated by commas"
            className="form-control"
            required
          />
          <small className="form-text text-muted">
            Separate multiple keywords with commas
          </small>
        </div>

        {formData.target_keywords.length > 0 && (
          <div className="keywords-display">
            <label>Current Keywords:</label>
            <div className="keyword-tags">
              {formData.target_keywords.map((keyword, index) => (
                <span key={index} className="keyword-tag">
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                    className="remove-keyword"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="suggested-topics">
          <label>Suggested Topics:</label>
          <div className="topic-buttons">
            {suggestedTopicsList.map((topic, index) => (
              <button
                key={index}
                type="button"
                onClick={() => selectSuggestedTopic(topic)}
                className="topic-btn"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>

        {trendingKeywords.length > 0 && (
          <div className="trending-keywords">
            <label>Trending Keywords:</label>
            <div className="keyword-buttons">
              {trendingKeywords.slice(0, 10).map((keyword, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => addKeyword(keyword.keyword)}
                  className="keyword-btn"
                >
                  {keyword.keyword}
                  <span className="volume">({keyword.search_volume})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary generate-btn"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Generating Blog...
              </>
            ) : (
              <>
                <i className="fas fa-magic"></i>
                Generate Blog
              </>
            )}
          </button>
        </div>
      </form>

      {isGenerating && (
        <div className="generation-progress">
          <div className="progress">
            <div
              className="progress-bar"
              style={{ width: `${generationProgress}%` }}
            ></div>
          </div>
          <p>Generating your blog content... {generationProgress}%</p>
        </div>
      )}

      <div className="generation-info">
        <h4>How it works:</h4>
        <ol>
          <li>Enter your blog topic and target keywords</li>
          <li>Our AI analyzes your clinic's information and location</li>
          <li>LangChain retrieves relevant dental knowledge from our RAG system</li>
          <li>GPT-4 generates SEO-optimized content with FAQs and CTAs</li>
          <li>Content is automatically saved as a draft for review</li>
        </ol>
      </div>
    </div>
  );
};

export default BlogGenerationForm;
